/**
 * Does the signed-in user have an employee record of their own, in this tenant?
 *
 * This is the whole basis of employee-portal access. It replaces the previous
 * rule, which was `profile.role === 'employee'` — a rule that made the portal
 * unreachable for the people most likely to need it, because an owner who also
 * works at the company holds `role = 'owner'` and was redirected away from
 * their own leave, documents and details.
 *
 * The rule now is the true one: you may open the employee portal when there is
 * an employee record linked to your account, in your tenant, and it is active.
 * Nothing about your administrative role grants or withholds it.
 *
 * WHAT "ACTIVE" MEANS, AND WHY IT IS CHECKED HERE
 *   An archived, offboarded or erased employee is a historical record, not a
 *   person with a current employee experience. Letting one through would give
 *   a departed colleague a live view of documents and balances. If an
 *   offboarding journey is added later that needs portal access for leavers,
 *   this is the one place that decision belongs — it is not scattered across
 *   pages.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO
 *   It does not grant anything. Every leave and document RPC re-derives the
 *   caller's authority server-side from auth.uid(); opening a portal changes
 *   nothing about what the database will do for you. An owner in their own
 *   employee view is, to the database, exactly the employee they are.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useCurrentTenantId } from '../contexts/TenantContext';

export interface EmployeeSelfRecord {
  id: string;
  name: string;
  status: string | null;
  onboarding_status: string | null;
  erased_at: string | null;
}

export type EmployeeSelfState =
  /** Still resolving — the tenant or the query has not answered yet. */
  | 'loading'
  /** No employee record linked to this account in this tenant. */
  | 'none'
  /** A record exists but is archived, offboarded or erased. */
  | 'inactive'
  /** A live employee record. Portal access. */
  | 'active'
  /** The lookup failed. Distinct from 'none': we do not know. */
  | 'error';

export interface UseEmployeeSelfResult {
  state: EmployeeSelfState;
  employee: EmployeeSelfRecord | null;
  refetch: () => void;
}

/** A record that no longer represents someone currently employed here. */
export function isActiveEmployeeRecord(e: EmployeeSelfRecord | null): boolean {
  if (!e) return false;
  if (e.erased_at) return false;
  if (e.onboarding_status === 'archived') return false;
  if (e.status === 'offboarded') return false;
  return true;
}

/**
 * @param enabled Pass false to skip the query entirely. Routes that are not the
 *                employee portal should not pay for this lookup, and hooks
 *                cannot be called conditionally.
 */
export function useEmployeeSelf(enabled = true): UseEmployeeSelfResult {
  const tenantId = useCurrentTenantId();
  const [state, setState] = useState<EmployeeSelfState>('loading');
  const [employee, setEmployee] = useState<EmployeeSelfRecord | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick(t => t + 1), []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    (async () => {
      // No tenant yet is "not known", not "nothing there".
      if (!tenantId) {
        if (!cancelled) setState('loading');
        return;
      }
      if (!cancelled) setState('loading');

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          if (!cancelled) { setEmployee(null); setState('none'); }
          return;
        }

        // Tenant-scoped as well as user-scoped. A stray employee row carrying
        // this user_id in another workspace must never become an identity here.
        const { data, error } = await supabase
          .from('employees')
          .select('id, name, status, onboarding_status, erased_at')
          .eq('user_id', session.user.id)
          .eq('tenant_id', tenantId)
          .maybeSingle();

        if (error) throw error;
        if (cancelled) return;

        const record = (data ?? null) as EmployeeSelfRecord | null;
        setEmployee(record);
        setState(!record ? 'none' : isActiveEmployeeRecord(record) ? 'active' : 'inactive');
      } catch {
        if (!cancelled) { setEmployee(null); setState('error'); }
      }
    })();

    return () => { cancelled = true; };
  }, [enabled, tenantId, tick]);

  return useMemo(() => ({ state, employee, refetch }), [state, employee, refetch]);
}
